import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import { FLOWS_ENUM } from '~/common/stores/ui/loginStore';
import HeaderGroup from '~/common/components/HeaderGroup';
import { SALES_PHONE_NUMBER_FORMATTED } from '~/common/utils/consts';
import Button from '../CommonButton';

// Import Local Styles
import styles from './LoginLmiModal.css';

@inject('accountInfoStore', 'annualUpgradeStore', 'convertToPaidStore', 'formsStore', 'loginStore', 'modalStore', 'trialSignupStore')
@observer
export default class LoginLmiModal extends Component {
    state = {
        errorMessage: null,
        tabRef: null
    };

    componentDidMount() {
        // Clicking the "Log In" hyperlink below opens the login screen in a new tab.
        // Once the user signs in, it will redirect them back to /signed-in (still in the new tab)
        // which will send a message back to this tab once the user is signed in successfully.
        window.addEventListener('message', this.messageListener);
    }

    componentWillUnmount() {
        window.removeEventListener('message', this.messageListener);
    }

    messageListener = (message) => {
        const { loginStore } = this.props;
        const { getLogInFlow } = loginStore;

        switch (message.data.action) {
            case 'getLmiModalInfo': {
                // The second tab will ask this first tab for some info (see /client/views/SignedIn/index.js)
                if (this.state.tabRef) {
                    this.state.tabRef.postMessage({ action: 'lmiModalInfo', logInFlow: getLogInFlow });
                }
                break;
            }
            case 'getSignedInMessage': {
                // The second tab will send lmiAuthcode to this first tab (see /client/views/SignedIn/index.js)
                const { lmiAuthcode, logInFlow } = message.data;
                this.onObtainedLmiAuthCode(lmiAuthcode, logInFlow);
                break;
            }
            default:
                break;
        }
    };

    onObtainedLmiAuthCode = (lmiAuthcode, logInFlow) => {
        try {
            const { loginStore } = this.props;
            if (lmiAuthcode) {
                loginStore.setLmiAuthcode(lmiAuthcode);
                switch (logInFlow) {
                    case FLOWS_ENUM.RUBY:
                    case FLOWS_ENUM.ANNUAL_UPGRADE:
                        this.handleGHToken(lmiAuthcode);
                        break;
                    case FLOWS_ENUM.TRIAL_UPGRADE:
                        this.handleTrialUpgradeLogInFlow(lmiAuthcode);
                        break;
                    default:
                        // Direct buy flow
                        this.handleLmiUserInfo(lmiAuthcode);
                }
            } else {
                // User must have just got signed out from the redirect
                // so now redirect the user back to the login screen
                this.handleLoginTab();
            }
        } catch (err) {
            // report a general error and alert user
            this.setState({
                errorMessage: 'There was a problem loading your account information.'
            });
        }
    };

    handleGHToken = () => {
        const { annualUpgradeStore, loginStore } = this.props;
        const { isAnnualUpgradeLogInFlow, isRubyLogInFlow } = loginStore;

        loginStore.authUser(() => {
            if (isAnnualUpgradeLogInFlow || isRubyLogInFlow) {
                annualUpgradeStore.getUserInfo(() => {
                    this.closeModal();
                    this.closeTab();
                });
            } else {
                this.setState({
                    errorMessage: 'There was a problem loading your account information.'
                });
            }
        });
    };

    handleTrialUpgradeLogInFlow = (lmiAuthcode) => {
        const { convertToPaidStore, loginStore } = this.props;
        const { isTrialUpgradeLogInFlow } = loginStore;
        if (isTrialUpgradeLogInFlow) {
            loginStore.setLmiAuthcode(lmiAuthcode);
            convertToPaidStore.authUser(() => {
                this.closeModal();
                this.closeTab();
            });
        } else {
            this.setState({
                errorMessage: 'There was a problem loading your account information.'
            });
        }
    };

    handleLmiUserInfo = () => {
        const { loginStore } = this.props;

        loginStore.loadLmiUserInfo((lmiUserInfo) => {
            if (lmiUserInfo) {
                this.closeModal();
                this.closeTab();
            } else {
                // User must have already been signed in (or code was invalid in some way) so we need a new code.
                // Redirect to signout page, which will then return to this page afterward,
                // but this time it won't have a ?code in the querystring
                this.handleLogoutTab();
            }
        });
    };

    onCanceledFreeTrial = (lmiAuthcode) => {
        const { convertToPaidStore, loginStore } = this.props;
        const { isTrialUpgradeLogInFlow } = loginStore;

        if (isTrialUpgradeLogInFlow) {
            loginStore.setLmiAuthcode(lmiAuthcode);
            convertToPaidStore.authUser(() => {
                this.closeModal();
                this.closeTab();
            });
        } else {
            this.setState({
                errorMessage: 'There was a problem loading your account information.'
            });
        }
    };

    closeModal = () => {
        const { modalStore } = this.props;
        modalStore.setModalOptions({ isModalOpen: false });
    };

    closeTab = () => {
        if (this.state.tabRef && !this.state.tabRef.closed) {
            this.state.tabRef.close();
            this.state.tabRef = null;
        }
    };

    handleLoginTab = () => {
        const { loginStore } = this.props;
        loginStore.clearLogin();
        this.state.tabRef = window.open('/util/login');
    };

    handleLogoutTab = () => {
        const { loginStore } = this.props;
        loginStore.clearLogin();
        this.state.tabRef = window.open('/util/logout');
    };

    render() {
        const { loginStore } = this.props;
        const { isRubyLogInFlow, isAnnualUpgradeLogInFlow, isTrialUpgradeLogInFlow } = loginStore;
        const headerText = isRubyLogInFlow || isAnnualUpgradeLogInFlow || isTrialUpgradeLogInFlow ? 'Please log in to continue' : 'Welcome back!';

        if (this.state.errorMessage) {
            return (
                <div className={styles.wrapper} id="trial__login-modal">
                    <HeaderGroup>
                        <h1 className={styles.header}>Error</h1>
                        <h2>{this.state.errorMessage}</h2>
                        <p className={styles.copy}>If the problem persists, please contact us at {SALES_PHONE_NUMBER_FORMATTED}.</p>
                    </HeaderGroup>
                </div>
            );
        }

        return (
            <div className={styles.wrapper} id="trial__login-modal">
                <div>
                    <h3 className={styles.header}>
                        {headerText}
                    </h3>
                    <form className={styles.form}>
                        {isRubyLogInFlow || isAnnualUpgradeLogInFlow || isTrialUpgradeLogInFlow ? (
                            <p className={styles.copy}>If you don't have a Grasshopper account, please <a href="/">sign up</a> first.</p>
                        ) : (
                            <p className={styles.copy}>Your email is already in the system. Please log in to continue.</p>
                        )}
                        <Button
                            id="login__LogOut"
                            modifier={styles.button}
                            onClick={this.handleLogoutTab}
                            type="button"
                        >Log In</Button>
                    </form>
                </div>
            </div>
        );
    }
}

